import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  createPersonalityTest,
  getPersonalityTestStatus,
  navigatePersonalityTest,
} from "../../../api";
import * as style from "./styles.module.scss";
import history from "../../../history";

const PersonalityTest = ({
  navigateBackPath,
  handlePersonalityTestCompleted,
  personalityTestState,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [testStatus, setTestStatus] = useState(personalityTestState);
  const [iframeHeight, setIframeHeight] = useState(300);
  const [currentQuestion, setCurrentQuestion] = useState(
    personalityTestState?.progress?.current_question || 0
  );
  const [currentAnswer, setCurrentAnswer] = useState("");
  const iframeRef = useRef(null);

  const checkTestStatus = async () => {
    const response = await getPersonalityTestStatus();
    setTestStatus(response.data);
  };

  useEffect(() => {
    checkTestStatus();
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "iframe-height") {
        const newHeight = Math.max(event.data.height, 300);
        setIframeHeight(newHeight);
      }

      if (event.data.type === "test-completed") {
        checkTestStatus();
      }

      if (event.data.type === "question-updated") {
        if (event.data.questionNumber !== currentQuestion) {
          setCurrentQuestion(event.data.questionNumber);
        }
        if (event.data.answer !== currentAnswer) {
          setCurrentAnswer(event.data.answer || "");
        }
      }

      if (event.data.type === "answer-selected") {
        if (event.data.answer !== currentAnswer) {
          setCurrentAnswer(event.data.answer);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const createTest = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await createPersonalityTest();
      if (response.data.success) {
        await checkTestStatus();
      } else {
        setError("Failed to create personality test. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleBackQuestion = async () => {
    if (loading) return;

    if (testStatus?.completed || currentQuestion <= 1) {
      history.push(navigateBackPath);
      return;
    }

    try {
      setLoading(true);
      const newQuestionNumber = currentQuestion - 1;
      setCurrentQuestion(newQuestionNumber);

      const response = await navigatePersonalityTest(
        testStatus?.token,
        "back",
        currentQuestion,
        currentAnswer
      );

      if (response.data) {
        if (iframeRef.current) {
          iframeRef.current.contentWindow.postMessage(
            {
              type: "navigate-to-question",
              questionNumber: response.data.question_number,
              question: response.data.question,
              answer: response.data.answer,
            },
            window.location.origin
          );
        }

        setCurrentQuestion(response.data.question_number);
        setCurrentAnswer(response.data.answer || "");
      }
    } catch (err) {
      setError("Failed to go back. Please try again.");
      setCurrentQuestion(currentQuestion);
    } finally {
      setLoading(false);
    }
  };

  const renderTestContent = () => {
    if (testStatus === null) {
      return (
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }

    if (!testStatus.has_test) {
      return (
        <div className="mb-3">
          <p className="mb-2">
            This short assessment is designed to give you valuable insights into
            your personality traits, communication style, and workplace
            preferences. Understanding these aspects can help you make more
            informed career choices, enhance team dynamics, and identify roles
            where you’re most likely to thrive.
          </p>
          <p>
            The test consists of <strong>60 quick questions</strong> and takes
            approximately <strong>7 minutes to complete.</strong> Please answer
            honestly and instinctively - there are no right or wrong answers.
          </p>
        </div>
      );
    }

    if (testStatus.test_url) {
      return (
        <div>
          {!testStatus?.completed && (
            <div className="mb-2" style={{ backgroundColor: "#E2E8F0" }}>
              <p className="p-2">
                <strong>
                  Rate how accurately each statement describes you. Be honest
                  and base your answers on how you truly are, not how you wish
                  to be.
                </strong>
              </p>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={testStatus.test_url}
            width="100%"
            title="Personality Test"
            allow="same-origin"
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
            style={{
              height: `${iframeHeight}px`,
              border: "none",
              borderRadius: "0.25rem",
              transition: "height 0.2s ease-out",
              overflow: "hidden",
              marginBottom: "40px",
            }}
          />
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`col-sm-12 col-lg-12 mt-2 mt-lg-4 mb-2 ${style.form}`}>
      <div>{renderTestContent()}</div>

      <div className={`mt-lg-auto mb-lg-2 ${style.actions}`}>
        {!testStatus?.completed && !testStatus?.has_test && (
          <>
            <Link to={navigateBackPath}>
              <button
                className="btn btn-outline-info text-dark mr-1"
                type="button"
              >
                Back
              </button>
            </Link>
            <button
              className="btn btn-primary btn-block"
              style={{ width: "200px" }}
              onClick={createTest}
              disabled={loading}
            >
              {loading ? "Starting test..." : "Let's get started!"}
            </button>
          </>
        )}

        {testStatus?.has_test && (
          <>
            <button
              className={`btn btn-outline-info text-dark ${
                loading ? "disabled" : ""
              } ${testStatus?.completed ? "mr-1" : ""}`}
              type="button"
              onClick={loading ? undefined : handleBackQuestion}
              disabled={loading}
            >
              Back
            </button>
            {testStatus?.completed && (
              <button
                className={`btn btn-primary btn-block ${
                  testStatus?.completed ? "" : "disabled"
                }`}
                onClick={handlePersonalityTestCompleted}
              >
                Next
              </button>
            )}
          </>
        )}
      </div>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default PersonalityTest;
